import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Layers } from 'lucide-react';
import LanguageToggle from './LanguageToggle';

/**
 * Reusable legal page renderer driven entirely by `legal.json` namespace.
 * `pageKey` is one of: 'privacy' | 'terms' | 'smsCompliance'.
 * `extraFooterLinks` allows pages to render different footer link sets.
 */
export default function LegalPage({ pageKey, testId, extraFooterLinks }) {
  const { t } = useTranslation('legal');
  const sections = t(`${pageKey}.sections`, { returnObjects: true }) || [];
  const title = t(`${pageKey}.title`);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-500 hover:text-slate-800 text-sm">
            <ArrowLeft className="h-4 w-4" /> {t('common.backHome')}
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-md flex items-center justify-center" style={{ background: '#002FA7' }}>
                <Layers className="h-3.5 w-3.5 text-white" />
              </div>
              <span className="text-sm font-bold" style={{ color: '#002FA7', fontFamily: "'Outfit', sans-serif" }}>LocalBranding Pro</span>
            </Link>
            <LanguageToggle />
          </div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-4 py-12 sm:py-16" data-testid={testId}>
        <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{title}</h1>
        <p className="text-sm text-slate-500 mb-10">{t('common.lastUpdated')}</p>

        <div className="prose prose-slate max-w-none space-y-8 text-sm leading-relaxed text-slate-700">
          {sections.map((sec, idx) => (
            <section key={idx}>
              <h2 className="text-lg font-bold text-slate-900 mb-3">{sec.h}</h2>
              {sec.p && <p>{sec.p}</p>}
              {sec.subSections && sec.subSections.map((sub, j) => (
                <div key={j}>
                  <h3 className="font-semibold text-slate-800 mt-4 mb-2">{sub.subH}</h3>
                  <ul className="list-disc pl-5 space-y-1">
                    {sub.items.map((it, k) => (
                      <li key={k} dangerouslySetInnerHTML={{ __html: it }} />
                    ))}
                  </ul>
                </div>
              ))}
              {sec.items && (
                <ul className="list-disc pl-5 space-y-1 mt-2">
                  {sec.items.map((it, k) => (
                    <li key={k} dangerouslySetInnerHTML={{ __html: it }} />
                  ))}
                </ul>
              )}
              {sec.after && <p className="mt-2" dangerouslySetInnerHTML={{ __html: sec.after }} />}
              {sec.html && <p className="mt-2" dangerouslySetInnerHTML={{ __html: sec.html }} />}
            </section>
          ))}
        </div>
      </main>

      <footer className="border-t border-slate-200 py-8 text-center text-xs text-slate-400">
        <div className="max-w-4xl mx-auto px-4 flex flex-wrap justify-center gap-4">
          {(extraFooterLinks || []).map(({ to, labelKey }) => (
            <Link key={to} to={to} className="hover:text-slate-600">{t(`common.footer.${labelKey}`)}</Link>
          ))}
          <Link to="/" className="hover:text-slate-600">{t('common.footer.home')}</Link>
        </div>
        <p className="mt-3">&copy; {new Date().getFullYear()} LocalBranding Pro. {t('common.footer.rights')}</p>
      </footer>
    </div>
  );
}
